import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
      {message}
    </div>
  );
}

const TYPE_META = {
  window_open: {
    name: 'Payroll Window Open', icon: '📬', tone: 'sky', tint: 'bg-sky-50 ring-sky-200',
    description: 'Notifies clients when their payroll period opens for submissions.',
    daysLabel: 'days before deadline', timelineLabel: 'Window opens',
    minDays: 1, maxDays: 30,
  },
  reminder: {
    name: 'Payroll Reminder', icon: '🔔', tone: 'amber', tint: 'bg-amber-50 ring-amber-200',
    description: "Reminds clients who haven't submitted yet as the deadline approaches.",
    daysLabel: 'days before deadline', timelineLabel: 'Reminder',
    minDays: 1, maxDays: 15,
  },
  final_reminder: {
    name: 'Final Reminder', icon: '🚨', tone: 'rose', tint: 'bg-rose-50 ring-rose-200',
    description: 'A last-chance reminder sent on the deadline day itself.',
    anchored: true, timelineLabel: 'Final reminder',
    minDays: 0, maxDays: 0,
  },
  escalation: {
    name: 'Overdue Escalation', icon: '⚠️', tone: 'rose', tint: 'bg-rose-50 ring-rose-200',
    description: "Sent to clients who missed their deadline and still haven't submitted.",
    daysLabel: 'days after deadline', timelineLabel: 'Escalation', after: true,
    minDays: 1, maxDays: 14,
  },
};

const EVENT_ORDER = ['window_open', 'reminder', 'final_reminder', 'escalation'];
const TONE_COLOR = { sky: '#0ea5e9', amber: '#f59e0b', rose: '#f43f5e' };

// A live, horizontal timeline of when emails fire relative to the deadline (D-0).
function ScheduleTimeline({ events }) {
  if (events.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-6 py-5 text-[13px] text-gray-500">
        <h4 className="text-[14px] font-bold uppercase tracking-[0.12em] text-ink dark:text-gray-100 mb-2">Send sequence — preview</h4>
        No emails are currently enabled.
      </div>
    );
  }

  const offsets = events.map(e => e.offset);
  const lo = Math.floor(Math.min(-10, ...offsets, 0) / 2) * 2;
  const hi = Math.ceil(Math.max(5, ...offsets, 0) / 2) * 2;
  const span = hi - lo || 1;
  const pctFor = d => ((d - lo) / span) * 100;

  const ticks = [];
  for (let d = lo; d <= hi; d += 2) ticks.push(d);

  // Stagger event labels onto two rows so close markers don't collide.
  const sorted = [...events].sort((a, b) => a.offset - b.offset);
  const rowOf = {};
  sorted.forEach((e, i) => { rowOf[e.type] = i % 2; });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-6 py-5">
      <div className="flex items-baseline justify-between mb-4">
        <h4 className="text-[14px] font-bold uppercase tracking-[0.12em] text-ink dark:text-gray-100">Send sequence — preview</h4>
        <span className="text-[12px] text-gray-500">Anchor: client's submission deadline</span>
      </div>
      <div className="relative" style={{ height: 120 }}>
        {/* Horizontal gradient track */}
        <div className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-sky-200 via-amber-200 to-rose-200 rounded" style={{ top: 24 }} />
        {/* Deadline anchor: label sits above the track; the rose line runs through both the track and the event-marker row */}
        <div className="absolute" style={{ left: `${pctFor(0)}%`, top: 0, transform: 'translateX(-50%)' }}>
          <div className="text-[11px] font-bold text-rose-600 uppercase tracking-wider whitespace-nowrap text-center">Deadline</div>
        </div>
        <div className="absolute bg-rose-500" style={{ left: `${pctFor(0)}%`, top: 16, width: 2, height: 60, transform: 'translateX(-50%)' }} />
        {/* Day ticks — small dots just under the track, with their labels directly beneath */}
        {ticks.map(d => (
          <div key={d} className="absolute" style={{ left: `${pctFor(d)}%`, top: 30, transform: 'translateX(-50%)' }}>
            <div className="w-1 h-1 rounded-full bg-gray-300 mx-auto" />
            <div className="text-[10px] text-gray-400 tabular mt-1.5 text-center" style={{ minWidth: 28 }}>
              {d === 0 ? 'D-0' : d > 0 ? `+${d}d` : `${d}d`}
            </div>
          </div>
        ))}
        {/* Event markers — pushed clearly below the tick-label band so they don't collide */}
        {events.map(e => (
          <div key={e.type} className="absolute" style={{ left: `${pctFor(e.offset)}%`, top: 62, transform: 'translateX(-50%)' }}>
            <div className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-md mx-auto" style={{ background: TONE_COLOR[e.tone] }} />
            <div className="text-[11px] font-semibold whitespace-nowrap text-center"
              style={{ color: TONE_COLOR[e.tone], marginTop: rowOf[e.type] === 1 ? 22 : 6 }}>
              {e.emoji} {e.label}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[12px] text-gray-500 mt-1">The scheduler runs hourly. Emails fire once per company per period — duplicates are prevented automatically.</p>
    </div>
  );
}

export default function EmailSchedulePage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [toast, setToast] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [edits, setEdits] = useState({});

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/admin/email-schedule');
      setConfigs(res.data);
    } catch (err) {
      setToast({ message: 'Failed to load schedule config', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleToggle = async (type, currentEnabled) => {
    setSaving(type);
    try {
      const cfg = configs.find(c => c.email_type === type);
      await api.put(`/admin/email-schedule/${type}`, { ...cfg, enabled: !currentEnabled });
      setToast({ message: `${TYPE_META[type].name} ${!currentEnabled ? 'enabled' : 'disabled'}`, type: 'success' });
      await fetchConfig();
    } catch (err) {
      setToast({ message: 'Failed to update', type: 'error' });
    } finally {
      setSaving(null);
    }
  };

  const handleSaveDays = async (type) => {
    const edit = edits[type];
    if (edit === undefined) return;
    const cfg = configs.find(c => c.email_type === type);
    setSaving(type);
    try {
      const days = type === 'escalation' ? -Math.abs(parseInt(edit)) : Math.abs(parseInt(edit));
      await api.put(`/admin/email-schedule/${type}`, { ...cfg, days_before_deadline: days });
      setEdits(prev => { const n = { ...prev }; delete n[type]; return n; });
      setToast({ message: `${TYPE_META[type].name} timing updated`, type: 'success' });
      await fetchConfig();
    } catch (err) {
      setToast({ message: 'Failed to update timing', type: 'error' });
    } finally {
      setSaving(null);
    }
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    setRunResult(null);
    try {
      const res = await api.post('/admin/email-schedule/run-now');
      setRunResult(res.data);
      setToast({ message: res.data.sent > 0 ? `Sent ${res.data.sent} email(s)` : 'No emails to send right now', type: 'success' });
    } catch (err) {
      setToast({ message: 'Scheduler run failed', type: 'error' });
    } finally {
      setRunningNow(false);
    }
  };

  const fetchHistory = async () => {
    if (showHistory) { setShowHistory(false); return; }
    setHistoryLoading(true);
    try {
      const res = await api.get('/admin/email-schedule/history');
      setHistory(res.data);
      setShowHistory(true);
    } catch (err) {
      setToast({ message: 'Failed to load history', type: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Display value (absolute days) for a config's input, honouring unsaved edits.
  const displayDaysFor = (cfg) => {
    if (edits[cfg.email_type] !== undefined) return edits[cfg.email_type];
    return Math.abs(cfg.days_before_deadline || 0);
  };

  // Signed offset on the timeline axis (negative = before deadline, positive = after).
  const offsetFor = (cfg) => {
    const meta = TYPE_META[cfg.email_type] || {};
    if (meta.anchored) return 0;
    const raw = parseInt(displayDaysFor(cfg));
    const abs = Number.isFinite(raw) ? Math.abs(raw) : 0;
    return meta.after ? abs : -abs;
  };

  const timelineEvents = configs
    .filter(c => c.enabled)
    .map(c => ({
      type: c.email_type,
      offset: offsetFor(c),
      emoji: TYPE_META[c.email_type]?.icon,
      label: TYPE_META[c.email_type]?.timelineLabel,
      tone: TYPE_META[c.email_type]?.tone || 'sky',
    }))
    .filter(e => e.label);

  const orderedConfigs = [...configs].sort(
    (a, b) => EVENT_ORDER.indexOf(a.email_type) - EVENT_ORDER.indexOf(b.email_type)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-[28px] leading-none text-ink dark:text-gray-100">Email Schedule</h1>
          <p className="text-gray-500 dark:text-gray-400 text-[14.5px] mt-2 max-w-[640px]">
            When automated reminder emails fire relative to each client's submission deadline. Toggles take effect immediately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchHistory} disabled={historyLoading}
            className="px-3 py-2 rounded-md bg-white dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 text-[13.5px] font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
            {historyLoading ? 'Loading…' : showHistory ? 'Hide history' : 'View send history'}
          </button>
          <button onClick={handleRunNow} disabled={runningNow}
            className="px-4 py-2 rounded-md bg-brand-600 text-white text-[13.5px] font-semibold hover:bg-brand-700 inline-flex items-center gap-1.5 disabled:opacity-50">
            {runningNow ? (
              <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><polygon points="5 3 19 12 5 21 5 3" /></svg>
            )}
            Run now
          </button>
        </div>
      </header>

      {/* Run result */}
      {runResult && (
        <div className="mb-5 p-3 bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 rounded-lg">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-green-600 dark:text-green-400 font-medium">{runResult.sent} sent</span>
            <span className="text-gray-500">{runResult.skipped} skipped</span>
            {runResult.errors > 0 && <span className="text-red-600 dark:text-red-400">{runResult.errors} errors</span>}
            <button onClick={() => setRunResult(null)} className="ml-auto text-xs text-gray-400 hover:text-gray-600">Dismiss</button>
          </div>
          {runResult.details && runResult.details.length > 0 && (
            <div className="mt-2 space-y-1">
              {runResult.details.map((d, i) => (
                <p key={i} className="text-xs text-gray-600 dark:text-gray-400"><span className="font-medium">{d.type}</span> → {d.company} ({d.recipients.join(', ')})</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Timeline */}
      <ScheduleTimeline events={timelineEvents} />

      {/* Tinted config cards */}
      <div className="mt-5 space-y-3">
        {orderedConfigs.map(cfg => {
          const meta = TYPE_META[cfg.email_type] || {};
          const hasEdit = edits[cfg.email_type] !== undefined;
          return (
            <div key={cfg.email_type}
              className={`rounded-xl ring-1 p-5 transition-opacity ${meta.tint || 'bg-white ring-gray-200'} ${cfg.enabled ? '' : 'opacity-60'}`}>
              <div className="flex items-center gap-4">
                <span className="text-[26px] leading-none">{meta.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-[16px] font-bold text-ink">{meta.name}</h4>
                    {!cfg.enabled && <span className="text-[11.5px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600 ring-1 ring-gray-200">Off</span>}
                  </div>
                  <p className="text-[13px] text-gray-600 mt-0.5">{meta.description}</p>
                </div>
                <button
                  onClick={() => handleToggle(cfg.email_type, cfg.enabled)}
                  disabled={saving === cfg.email_type}
                  className={`relative shrink-0 rounded-full transition-colors disabled:opacity-50 ${cfg.enabled ? 'bg-brand-600' : 'bg-gray-300'}`}
                  style={{ width: 44, height: 24 }}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${cfg.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {cfg.enabled && meta.anchored && (
                <p className="mt-3 pt-3 border-t border-gray-200/60 text-[12.5px] text-gray-500 italic">
                  Sent on the deadline day — no configuration needed.
                </p>
              )}

              {cfg.enabled && !meta.anchored && (
                <div className="mt-4 pt-4 border-t border-gray-200/60 flex flex-wrap items-center gap-3 text-[13.5px]">
                  <span className="text-gray-600">Send</span>
                  <input
                    type="number"
                    min={meta.minDays}
                    max={meta.maxDays}
                    value={displayDaysFor(cfg)}
                    onChange={(e) => setEdits(prev => ({ ...prev, [cfg.email_type]: e.target.value }))}
                    className="w-16 rounded-md border-gray-300 text-center text-[14px] tabular py-1 focus:border-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-gray-600">{meta.daysLabel}</span>
                  {hasEdit && (
                    <button
                      onClick={() => handleSaveDays(cfg.email_type)}
                      disabled={saving === cfg.email_type}
                      className="px-3 py-1 text-xs font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                      {saving === cfg.email_type ? 'Saving…' : 'Save'}
                    </button>
                  )}
                  {cfg.updated_by_name && (
                    <span className="ml-auto text-[11.5px] text-gray-400">
                      Last updated by {cfg.updated_by_name}, {new Date(cfg.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Send history */}
      {showHistory && (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Send History</h3>
            <button onClick={() => setShowHistory(false)} className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">Hide</button>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No emails sent yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                    <th className="pb-2 pr-4">Date</th>
                    <th className="pb-2 pr-4">Type</th>
                    <th className="pb-2 pr-4">Company</th>
                    <th className="pb-2 pr-4">Recipients</th>
                    <th className="pb-2">Trigger</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {history.map(h => (
                    <tr key={h.id} className="text-gray-700 dark:text-gray-300">
                      <td className="py-2 pr-4 whitespace-nowrap">{new Date(h.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="py-2 pr-4">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          h.email_type === 'escalation' ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400'
                            : h.email_type === 'reminder' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                              : h.email_type === 'final_reminder' ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400'
                                : 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400'
                        }`}>{h.email_type.replace('_', ' ')}</span>
                      </td>
                      <td className="py-2 pr-4">{h.company_name}</td>
                      <td className="py-2 pr-4 text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">{h.recipients}</td>
                      <td className="py-2"><span className={`text-xs ${h.trigger === 'auto' ? 'text-gray-400' : 'text-brand-600 dark:text-brand-400'}`}>{h.trigger}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
